import React, { useState } from 'react';
import { Input, Popover, Row, Col, Button, Space, Tabs } from 'antd';
import * as Icons from '@ant-design/icons';

// よく使うアイコンのカテゴリ分け
const ICON_CATEGORIES = {
  facility: [
    'ApartmentOutlined',
    'HomeOutlined',
    'BankOutlined',
    'ShopOutlined',
    'ShoppingOutlined',
    'ShoppingCartOutlined',
    'CoffeeOutlined',
    'RestOutlined',
    'CrownOutlined',
    'MedicineBoxOutlined',
    'TeamOutlined',
    'UsergroupAddOutlined',
    'IdcardOutlined',
    'SolutionOutlined',
    'ReadOutlined',
    'BookOutlined',
    'ClusterOutlined',
    'EnvironmentOutlined',
    'SafetyOutlined',
    'BuildOutlined',
  ],
  equipment: [
    'ToolOutlined',
    'BuildOutlined',
    'SettingOutlined',
    'ThunderboltOutlined',
    'FireOutlined',
    'ExperimentOutlined',
    'ApiOutlined',
    'BulbOutlined',
    'RocketOutlined',
    'DatabaseOutlined',
    'CameraOutlined',
    'LaptopOutlined',
    'WifiOutlined',
    'RadarChartOutlined',
  ],
  industry: [
    'HomeOutlined',
    'ShopOutlined',
    'BankOutlined',
    'CarOutlined',
    'TruckOutlined',
    'DashboardOutlined',
    'SlackOutlined',
    'BgColorsOutlined',
    'BoxPlotOutlined',
    'FundOutlined',
    'CloudOutlined',
    'GlobalOutlined',
    'InboxOutlined',
  ],
  general: [
    'AppstoreOutlined',
    'StarOutlined',
    'HeartOutlined',
    'TagOutlined',
    'FlagOutlined',
    'BookOutlined',
    'FileOutlined',
    'FolderOutlined',
    'SafetyOutlined',
    'AlertOutlined',
    'NotificationOutlined',
    'CalendarOutlined',
    'ClockCircleOutlined',
    'CompassOutlined',
    'EnvironmentOutlined',
  ],
  shapes: [
    'CheckCircleOutlined',
    'CloseCircleOutlined',
    'InfoCircleOutlined',
    'QuestionCircleOutlined',
    'StopOutlined',
    'WarningOutlined',
    'MinusCircleOutlined',
    'PlusCircleOutlined',
    'PlayCircleOutlined',
    'PauseCircleOutlined',
  ],
};

type IconRegistry = typeof Icons;
type IconName = keyof IconRegistry;
type AntIconComponent = React.ComponentType<React.ComponentProps<typeof Icons.HomeOutlined>>;

const isIconName = (iconName: string): iconName is IconName =>
  iconName in Icons;

interface IconPickerProps {
  value?: string;
  onChange?: (iconName: string) => void;
  placeholder?: string;
}

export const IconPicker: React.FC<IconPickerProps> = ({
  value,
  onChange,
  placeholder = 'アイコンを選択',
}) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const handleIconSelect = (iconName: string) => {
    onChange?.(iconName);
    setOpen(false);
  };

  const handleClear = () => {
    onChange?.('');
    setOpen(false);
  };

  // アイコンコンポーネントを取得
  const getIconComponent = (iconName: string) => {
    if (!isIconName(iconName)) {
      return null;
    }

    const iconMap = Icons as unknown as Record<string, AntIconComponent>;
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent style={{ fontSize: 24 }} /> : null;
  };

  // 選択されたアイコンを表示
  const SelectedIcon = value ? getIconComponent(value) : null;

  // 検索フィルター
  const filterIcons = (icons: readonly string[]) => {
    if (!searchText) return icons;
    return icons.filter((icon) =>
      icon.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  const renderIconButtons = (icons: readonly string[]) => (
    <Row gutter={[8, 8]}>
      {filterIcons(icons).map((iconName) => {
        if (!isIconName(iconName)) {
          return null;
        }

        const iconMap = Icons as unknown as Record<string, AntIconComponent>;
        const IconComponent = iconMap[iconName];
        if (!IconComponent) {
          return null;
        }
        return (
          <Col span={6} key={iconName}>
            <Button
              type={value === iconName ? 'primary' : 'default'}
              icon={<IconComponent />}
              onClick={() => handleIconSelect(iconName)}
              style={{ width: '100%' }}
              title={iconName}
            />
          </Col>
        );
      })}
    </Row>
  );

  const iconPickerContent = (
    <div style={{ width: 400, maxHeight: 500, overflow: 'auto' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Input
          placeholder="アイコンを検索"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />

        <Tabs
          items={[
            {
              key: 'facility',
              label: '施設',
              children: renderIconButtons(ICON_CATEGORIES.facility),
            },
            {
              key: 'equipment',
              label: '設備',
              children: renderIconButtons(ICON_CATEGORIES.equipment),
            },
            {
              key: 'industry',
              label: '産業',
              children: renderIconButtons(ICON_CATEGORIES.industry),
            },
            {
              key: 'general',
              label: '一般',
              children: renderIconButtons(ICON_CATEGORIES.general),
            },
            {
              key: 'shapes',
              label: '図形',
              children: renderIconButtons(ICON_CATEGORIES.shapes),
            },
          ]}
        />

        <Button block onClick={handleClear} danger>
          クリア
        </Button>
      </Space>
    </div>
  );

  return (
    <Popover
      content={iconPickerContent}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
    >
      <Button
        style={{
          width: '100%',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Space>
          {SelectedIcon || <span style={{ color: '#999' }}>{placeholder}</span>}
          {value && <span style={{ fontSize: 12, color: '#999' }}>{value}</span>}
        </Space>
      </Button>
    </Popover>
  );
};

